/**
 * FinanceReports — AP/AR position from live /v1/finance/stats: a KPI row
 * (payable + receivable outstanding/overdue, issued-this-month throughput),
 * a "what needs attention" insight list, and two stage donuts (payables
 * pipeline, receivables lifecycle).
 *
 * Data is honest at the source: everything renders from the server stats
 * payload. The AR `issued` block is optional on the wire (an API that
 * predates it), so the receivable half degrades to zeros/"no data" rather
 * than fabricating. All colours flow from theme/tokens.ts — no raw hex here.
 */
import { useCallback, useEffect, useState } from 'react'
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts'
import {
  SectionTitle,
  LoadingState,
  ErrorState,
  EmptyState,
} from '../../DashboardShared'
import { FinanceMetric } from './FinanceOverview'
import { fetchFinanceStats } from '../../api'
import type { FinanceStats, IssuedInvoiceStats } from '../../types'
import { apiErrorMessage } from '../../../../api/client'
import { chartCategorical, shadow, surface } from '../../../../theme/tokens'
import { nonGbpNote, withNonGbpNote } from '../../../../lib/money'

function money(n: number, currency = 'GBP'): string {
  const sym = currency === 'GBP' ? '£' : currency === 'USD' ? '$' : currency === 'EUR' ? '€' : ''
  return `${sym}${n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

function plural(n: number, one: string, many: string): string {
  return n === 1 ? one : many
}

type Slice = { name: string; value: number; fill: string }

// Palette slot is fixed per stage BEFORE zero stages are dropped — filtering
// must not repaint the surviving slices as data moves through the lifecycle.
function slices(entries: [string, number][]): Slice[] {
  return entries
    .map(([name, value], i) => ({ name, value, fill: chartCategorical[i % chartCategorical.length] }))
    .filter(s => s.value > 0)
}

function buildInsights(ap: FinanceStats['invoices'], ar?: IssuedInvoiceStats): string[] {
  const out: string[] = []
  if (ap.overdueCount > 0) {
    out.push(`${ap.overdueCount} of ${ap.total} payables are past their due date.`)
  }
  if (ap.pendingReview > 0) {
    // The fenced sum can leave rows out (the currency fence) — the sentence
    // names them rather than pairing a whole count with a partial total.
    const excl = nonGbpNote(ap.pendingValueExcludedNonGbp, 'invoice')
    out.push(
      `${ap.pendingReview} ${plural(ap.pendingReview, 'invoice', 'invoices')} awaiting review — ${money(ap.pendingValue)} not yet approved${excl ? ` (${excl})` : ''}.`,
    )
  }
  if (ar && ar.overdueCount > 0) {
    const excl = nonGbpNote(ar.overdueExcludedNonGbp, 'invoice')
    out.push(
      `${money(ar.overdueValue)} receivable is past due across ${ar.overdueCount} ${plural(ar.overdueCount, 'invoice', 'invoices')}${excl ? ` (${excl})` : ''}.`,
    )
  }
  if (ar && ar.draft > 0) {
    out.push(`${ar.draft} issued ${plural(ar.draft, 'invoice', 'invoices')} still in draft — not sent to a customer yet.`)
  }
  return out
}

function DonutCard({ title, subtitle, data }: { title: string; subtitle: string; data: Slice[] }) {
  return (
    <div className="k-card">
      <div className="k-card-head">
        <div>
          <div className="k-card-title">{title}</div>
          <div className="k-card-sub">{subtitle}</div>
        </div>
      </div>
      {data.length === 0 ? (
        <EmptyState
          message="Nothing to chart yet"
          hint="This fills in once invoices reach these stages."
        />
      ) : (
        <div className="px-5 pb-5">
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={data}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="55%"
                  outerRadius="80%"
                  paddingAngle={2}
                >
                  {data.map(slice => (
                    <Cell key={slice.name} fill={slice.fill} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{ background: surface.card, border: 'none', borderRadius: 10, fontSize: 12, boxShadow: shadow.tooltip }}
                  formatter={(v: number) => `${v} ${plural(v, 'invoice', 'invoices')}`}
                />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1">
            {data.map(slice => (
              <li key={slice.name} className="flex items-center gap-2 text-12 text-ink-secondary">
                <span
                  className="inline-block h-2 w-2 rounded-full flex-shrink-0"
                  style={{ backgroundColor: slice.fill }}
                />
                {slice.name}
                <span className="font-medium text-ink-primary">{slice.value}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default function FinanceReports() {
  const [stats, setStats] = useState<FinanceStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      setStats(await fetchFinanceStats())
    } catch (e) {
      setError(apiErrorMessage(e, 'Could not load finance reports'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    void load()
  }, [load])

  if (loading) return <LoadingState rows={6} />
  if (error) return <ErrorState message={error} onRetry={load} />
  if (!stats) {
    return (
      <EmptyState
        message="No finance data yet"
        hint="Reports fill in as invoices are received and issued."
      />
    )
  }

  const ap = stats.invoices
  const ar = stats.issued

  const payablePipeline = slices([
    ['Pending review', ap.pendingReview],
    ['Approved', ap.approved],
    ['Paid', ap.paid],
    ['Rejected', ap.rejected],
  ])

  const receivableLifecycle = ar
    ? slices([
        ['Draft', ar.draft],
        ['Issued', ar.issued],
        ['Sent', ar.sent],
        ['Paid', ar.paid],
        ['Void', ar.void],
      ])
    : []

  const insights = buildInsights(ap, ar)

  return (
    <div className="page-enter flex flex-col gap-4 mx-auto" style={{ maxWidth: 1120, minWidth: 1040 }}>
      <SectionTitle
        title="Reports"
        subtitle="Money owed out and owed in, from live invoice data."
      />

      <div className="grid grid-cols-5 gap-3">
        <FinanceMetric
          label="Payable outstanding"
          value={money(ap.payableOutstanding)}
          note={withNonGbpNote(`${ap.payableOutstandingCount} awaiting payment`, ap.payableOutstandingExcludedNonGbp, 'invoice')}
        />
        <FinanceMetric
          label="Payables overdue"
          value={ap.overdueCount}
          note="past due date"
          tone={ap.overdueCount > 0 ? 'amber' : 'default'}
        />
        <FinanceMetric
          label="Receivable outstanding"
          value={money(ar?.outstandingValue ?? 0)}
          note={ar ? withNonGbpNote(`${ar.issued + ar.sent} awaiting payment`, ar.outstandingExcludedNonGbp, 'invoice') : 'no data'}
        />
        <FinanceMetric
          label="Receivables overdue"
          value={money(ar?.overdueValue ?? 0)}
          note={withNonGbpNote(`${ar?.overdueCount ?? 0} past due`, ar?.overdueExcludedNonGbp, 'invoice')}
          tone={(ar?.overdueCount ?? 0) > 0 ? 'amber' : 'default'}
        />
        <FinanceMetric
          label="Issued this month"
          value={money(ar?.issuedThisMonthValue ?? 0)}
          note={withNonGbpNote(`${ar?.issuedThisMonth ?? 0} ${plural(ar?.issuedThisMonth ?? 0, 'invoice', 'invoices')}`, ar?.issuedThisMonthExcludedNonGbp, 'invoice')}
        />
      </div>

      {insights.length > 0 && (
        <div className="k-card">
          <div className="k-card-head">
            <div className="k-card-title">What needs attention</div>
          </div>
          <ul className="px-5 pb-5 pt-4 flex flex-col gap-1">
            {insights.map(line => (
              <li key={line} className="text-13 text-ink-body">
                {line}
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="grid grid-cols-2 gap-4">
        <DonutCard
          title="Payables pipeline"
          subtitle="Invoices received, by stage"
          data={payablePipeline}
        />
        <DonutCard
          title="Receivables lifecycle"
          subtitle="Invoices issued, by stage"
          data={receivableLifecycle}
        />
      </div>
    </div>
  )
}
